import React, { useEffect } from 'react';
import type { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, Grid, Typography } from '@mui/material';
import { DatePickerField } from '~/components/DatePickerField/DatePickerField.tsx';
import { DATE_FORMAT, stringToDayjs } from '~/components/DatePickerField/dateUtils';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import type {
  TManagerChildPeriodOption,
  TManagerDocumentFilterOption,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import {
  CHILD_PERIOD_LABEL,
  CHILD_PERIOD_NO_OPTIONS_TEXT,
  CLEAR_TERM_FILTER_BUTTON_LABEL,
  DATE_PLACEHOLDER,
  DOCUMENT_PERIOD_FILTER_TITLE,
  DOCUMENT_TYPE_LABEL,
  DOCUMENT_TYPE_LOADING_TEXT,
  DOCUMENT_TYPE_NO_OPTIONS_TEXT,
  MANAGER_CHILD_PERIOD_OPTIONS,
  MANAGER_CHILD_PERIOD_OPTIONS_BY_VALUE,
  TERM_END_DATE_LABEL,
  TERM_START_DATE_LABEL,
  YEAR_PERIOD_LABEL,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';

interface IDocumentPeriodFilterProps {
  documentOptions: TManagerDocumentFilterOption[];
  isDocumentOptionsLoading: boolean;
  onDocumentOpen: () => void;
  selectedDocuments: TManagerDocumentFilterOption[];
  selectedYearPeriod: string;
  selectedChildPeriod: TManagerChildPeriodOption | null;
  termStartDate: Dayjs | null;
  termEndDate: Dayjs | null;
  onDocumentsChange: (value: TManagerDocumentFilterOption[]) => void;
  onYearPeriodChange: (value: string) => void;
  onChildPeriodChange: (value: TManagerChildPeriodOption | null) => void;
  onTermStartDateChange: (value: Dayjs | null) => void;
  onTermEndDateChange: (value: Dayjs | null) => void;
  onClearTermDates: () => void;
}

type TDocumentPeriodFilterForm = {
  documents: TManagerDocumentFilterOption[];
  yearPeriod: string;
  childPeriod: TManagerChildPeriodOption | null;
};

export const DocumentPeriodFilter: React.FC<IDocumentPeriodFilterProps> = ({
  documentOptions,
  isDocumentOptionsLoading,
  onDocumentOpen,
  selectedDocuments,
  selectedYearPeriod,
  selectedChildPeriod,
  termStartDate,
  termEndDate,
  onDocumentsChange,
  onYearPeriodChange,
  onChildPeriodChange,
  onTermStartDateChange,
  onTermEndDateChange,
  onClearTermDates,
}) => {
  const form = useForm<TDocumentPeriodFilterForm>({
    defaultValues: {
      documents: selectedDocuments,
      yearPeriod: selectedYearPeriod,
      childPeriod: selectedChildPeriod,
    },
  });
  const { setValue } = form;

  useEffect(() => {
    setValue('documents', selectedDocuments);
  }, [selectedDocuments, setValue]);

  useEffect(() => {
    setValue('yearPeriod', selectedYearPeriod);
  }, [selectedYearPeriod, setValue]);

  useEffect(() => {
    setValue('childPeriod', selectedChildPeriod);
  }, [selectedChildPeriod, setValue]);

  return (
    <FormProvider {...form}>
      <Box sx={managerComponentStyles.documentPeriodFilterSection}>
        <Typography
          variant="subtitle1"
          sx={managerComponentStyles.documentPeriodFilterTitle}
        >
          {DOCUMENT_PERIOD_FILTER_TITLE}
        </Typography>

        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.documentPeriodFilterField}>
              <FormAutocomplete
                fullWidth
                multiple
                name="documents"
                label={DOCUMENT_TYPE_LABEL}
                options={documentOptions}
                loading={isDocumentOptionsLoading}
                loadingText={DOCUMENT_TYPE_LOADING_TEXT}
                noOptionsText={DOCUMENT_TYPE_NO_OPTIONS_TEXT}
                onOpen={onDocumentOpen}
                onChange={(value) =>
                  onDocumentsChange(
                    Array.isArray(value) ? value : value ? [value] : []
                  )
                }
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.documentPeriodFilterField}>
              <FormAutocomplete
                fullWidth
                name="covenants-child-period-filter"
                label={CHILD_PERIOD_LABEL}
                options={MANAGER_CHILD_PERIOD_OPTIONS}
                noOptionsText={CHILD_PERIOD_NO_OPTIONS_TEXT}
                onChange={(value) => {
                  const selectedOption = Array.isArray(value) ? (value[0] ?? null) : value;
                  const childPeriod = selectedOption
                    ? MANAGER_CHILD_PERIOD_OPTIONS_BY_VALUE[selectedOption.value] ?? null
                    : null;

                  onChildPeriodChange(childPeriod);
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.documentPeriodFilterField}>
              <FormInput
                fullWidth
                name="yearPeriod"
                dataTestId="covenants-year-period-filter"
                label={YEAR_PERIOD_LABEL}
                placeholder="РРРР"
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                onChange={(event) =>
                  onYearPeriodChange(event.target.value.replace(/\D/g, '').slice(0, 4))
                }
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.datePickerField}>
              <DatePickerField
                label={TERM_START_DATE_LABEL}
                placeholder={DATE_PLACEHOLDER}
                date={termStartDate?.format(DATE_FORMAT) ?? null}
                onDateChange={(value) => onTermStartDateChange(stringToDayjs(value ?? undefined))}
                maxDate={termEndDate?.toDate() ?? null}
                dataTestId="covenants-term-start-date-filter"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.datePickerField}>
              <DatePickerField
                label={TERM_END_DATE_LABEL}
                placeholder={DATE_PLACEHOLDER}
                date={termEndDate?.format(DATE_FORMAT) ?? null}
                onDateChange={(value) => onTermEndDateChange(stringToDayjs(value ?? undefined))}
                minDate={termStartDate?.toDate() ?? null}
                dataTestId="covenants-term-end-date-filter"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={managerComponentStyles.documentPeriodFilterClearButton}>
              <Button
                variant="outlined"
                onClick={onClearTermDates}
                disabled={!termStartDate && !termEndDate}
                sx={managerComponentStyles.dateClearBtn}
              >
                {CLEAR_TERM_FILTER_BUTTON_LABEL}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};
